import styled from '@emotion/styled';
import * as React from 'react';

interface RuntimeVersionWidgetProps {
  children?: React.ReactNode;
  containerSince?: string;
  since?: string;
  updatedSince?: string;
}

interface ContainerVersionWidgetProps {
  children?: React.ReactNode;
  runtimeSince?: string;
  since?: string;
  updatedSince?: string;
}

export const RuntimeVersionWidget: React.FC<RuntimeVersionWidgetProps> = ({ containerSince, ...props }) => (
  <VersionCompatibilityWidget {...props} product="Runtime" companionSince={containerSince} />
);

export const ContainerVersionWidget: React.FC<ContainerVersionWidgetProps> = ({ runtimeSince, ...props }) => (
  <VersionCompatibilityWidget {...props} product="Container" companionSince={runtimeSince} />
);

interface VersionCompatibilityWidgetProps {
  children?: React.ReactNode;
  companionSince?: string;
  product: 'Runtime' | 'Container';
  since?: string;
  updatedSince?: string;
}

const VersionCompatibilityWidget: React.FC<VersionCompatibilityWidgetProps> = ({
  children,
  companionSince,
  product,
  since,
  updatedSince
}) => {
  const minimums = [
    since ? { label: product, version: since } : null,
    companionSince ? { label: product === 'Runtime' ? 'Container' : 'Runtime', version: companionSince } : null
  ].filter((item): item is { label: string; version: string } => item !== null);
  const tone = minimums.length ? 'info' : 'versionNote';

  return (
    <S.Root data-content-version-compatibility $tone={tone}>
      <S.Summary>
        <S.Label $tone={tone}>{minimums.length ? 'Version compatibility' : 'Version note'}</S.Label>
        {minimums.length || updatedSince ? (
          <S.Requirements>
            {minimums.map(({ label, version }) => (
              <S.Requirement key={label} $tone={tone}>
                <span>{label}</span>
                <strong>{version}+</strong>
              </S.Requirement>
            ))}
            {updatedSince ? (
              <S.Requirement $tone={tone}>
                <span>Updated in {product}</span>
                <strong>{updatedSince}</strong>
              </S.Requirement>
            ) : null}
          </S.Requirements>
        ) : null}
      </S.Summary>
      {children ? <S.Details data-content-version-details>{children}</S.Details> : null}
    </S.Root>
  );
};

namespace S {
  type Tone = 'info' | 'versionNote';

  export const Root = styled.aside<{ $tone: Tone }>`
    padding: 12px 16px;
    border: 1px solid ${(p) => p.theme.docsCallout[p.$tone].pillBorder};
    border-left: 3px solid ${(p) => p.theme.docsCallout[p.$tone].borderAccent};
    border-radius: ${(p) => p.theme.text.listRadius};
    background: ${(p) => p.theme.docsCallout[p.$tone].background};
    box-shadow: ${(p) => p.theme.text.blockquoteShadow};
    color: ${(p) => p.theme.text.paragraph};
  `;

  export const Summary = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px 14px;
  `;

  export const Label = styled.span<{ $tone: Tone }>`
    color: ${(p) => p.theme.docsCallout[p.$tone].pillText};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  `;

  export const Requirements = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `;

  export const Requirement = styled.span<{ $tone: Tone }>`
    display: inline-flex;
    gap: 6px;
    align-items: baseline;
    padding: 3px 8px;
    border: 1px solid ${(p) => p.theme.docsCallout[p.$tone].pillBorder};
    border-radius: 999px;
    font-size: 13px;

    strong {
      color: ${(p) => p.theme.text.heading};
      font-weight: 600;
    }
  `;

  export const Details = styled.div`
    margin-top: 12px;
    color: ${(p) => p.theme.text.paragraph};

    > :first-child {
      margin: 0;
    }
  `;
}
